import type { Editor } from '@tiptap/core';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useEffect, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';

interface HeadingDropdownMenuProps {
  editor: Editor;
  levels?: (1 | 2 | 3 | 4 | 5 | 6)[];
}

export const HeadingDropdownMenu = ({ editor, levels = [1, 2, 3, 4, 5, 6] }: HeadingDropdownMenuProps) => {
  // Track selection position to force re-render on cursor move
  const [, setSelectionPos] = useState(0);

  useEffect(() => {
    if (!editor) return;
    const updateSelection = () => {
      setSelectionPos(editor.state.selection.from);
    };
    editor.on('selectionUpdate', updateSelection);
    // Initial
    updateSelection();
    return () => {
      editor.off('selectionUpdate', updateSelection);
    };
  }, [editor]);

  const getCurrentHeadingLevel = (): number | null => {
    for (let i = 1; i <= 6; i++) {
      if (editor.isActive('heading', { level: i })) {
        return i;
      }
    }
    return null;
  };

  const setHeading = (level: 1 | 2 | 3 | 4 | 5 | 6) => {
    if (editor.isEditable) editor.chain().focus().toggleHeading({ level }).run();
  };

  const removeHeading = () => {
    if (editor.isEditable) editor.chain().focus().setParagraph().run();
  };

  const currentLevel = getCurrentHeadingLevel();
  const displayText = currentLevel ? `H${currentLevel}` : 'Paragraph';
  const selectedKey = currentLevel ? `heading-${currentLevel}` : 'paragraph';

  const menuItems: MenuProps['items'] = [
    {
      key: 'paragraph',
      label: (
        <>
          {selectedKey === 'paragraph' && <CheckOutlined style={{ marginRight: 4 }} />}
          Paragraph
        </>
      ),
      onClick: removeHeading
    },
    {
      type: 'divider'
    },
    ...levels.map((level) => ({
      key: `heading-${level}`,
      label: (
        <>
          {selectedKey === `heading-${level}` && <CheckOutlined style={{ marginRight: 4 }} />}
          {`Heading ${level}`}
        </>
      ),
      onClick: () => setHeading(level)
    }))
  ];

  return (
    <Dropdown.Button
      menu={{ items: menuItems }}
      type={currentLevel ? 'primary' : 'default'}
      title="Heading"
      disabled={!editor.isEditable}
    >
      {displayText}
    </Dropdown.Button>
  );
};
